// TEMPORARY legacy adapter: Vue 2 document form -> input of the shared
// customer-display modules. Pure functions (no Vue), so they can be unit tested.
// The shared allow-list (sanitizeView) is the real privacy boundary; this file
// only picks what to READ, and never hands the whole form onwards.

import { isPaymentQrOnCustomerDisplay } from '@shared/customer-display/paymentQr';
import { resolveDocumentStatus } from '@shared/customer-display/documentStatus';

const DOC_TYPES = ['quotation', 'invoice', 'receipt', 'debit-note', 'credit-note'];

function text(value) {
  return value === null || value === undefined ? '' : String(value).trim();
}

// PC1 (พรบ.) and PC2 (insurance) rows have no free-text name; the printed
// document builds it from the sub-category and sku (backend product_format).
function lineName(product) {
  const name = text(product.name);
  if (name) return name;
  if (product.category_id === 'PC1' || product.category_id === 'PC2') {
    return [text(product.sub_category_name), text(product.sku_code)].filter(Boolean).join(' ');
  }
  return '';
}

// Walk-in placeholder the pages put in customer_name; not a real owner name.
const WALK_IN_CUSTOMER = 'ลูกค้าหน้าร้าน';

// "(รย.12) รถจักรยานยนต์ส่วนบุคคล" -> "รย.12 รถจักรยานยนต์ส่วนบุคคล": the full name
// from the page's dictionary, minus the brackets around the code.
function carTypeLabel(carType, carTypeList) {
  const entry = (Array.isArray(carTypeList) ? carTypeList : []).find((item) => item && item.value === carType);
  return entry ? text(entry.name).replace(/^\(([^)]+)\)\s*/, '$1 ').trim() : '';
}

// Default duration (seconds) the configured post-rating content stays up
// before the display returns to idle. Mirrors the backend's own fallback
// (SERVICE_RATING_DEFAULT_DISPLAY_SECONDS) for a branch that saved the
// Service Rating setting before this field existed.
const DEFAULT_POST_RATING_SECONDS = 60;

export function branchIdentity(branch) {
  const source = branch || {};
  const setting = source.setting || {};
  const seconds = Number(setting.service_rating_display_seconds);
  return {
    name: text(source.name_for_receipt) || text(source.branch_name),
    logoUrl: text(source.logo_url),
    // Branch-level Service Rating post-rating content (Phase 2 settings) -
    // static, customer-facing by design (the owner wrote it to be read by
    // their own customers), so it travels on every view alongside the branch
    // identity rather than needing its own allow-list plumbing.
    postRatingTitle: text(setting.service_rating_thank_you_title),
    postRatingContent: text(setting.service_rating_thank_you_content),
    postRatingImageUrl: text(setting.service_rating_thank_you_image_url),
    postRatingDisplaySeconds: Number.isFinite(seconds) && seconds > 0 ? seconds : DEFAULT_POST_RATING_SECONDS
  };
}

// source = { docTypeId, form, totals, branch, printData, carTypeList }
//   carTypeList : the page's list.car_type_dict ([{ value, name }])
//   totals    : the page's total_price / total_discount / vat_price / net_price
//   printData : the page's `dataPrint` ({ main_bill, split_bill }) - only used to
//               read the saved QR payment snapshot for a document just saved.
export function extractDisplayInput(source) {
  const form = source.form || {};
  const branch = source.branch || {};
  const printBill = (source.printData && (source.printData.main_bill || source.printData.split_bill)) || null;
  const items = (Array.isArray(form.product) ? form.product : [])
    .map((product) => ({
      name: lineName(product || {}),
      price: Number(product && product.product_price) || 0,
      discount: Number(product && product.discount) || 0
    }))
    .filter((item) => item.name !== '');

  const totals = source.totals || {};
  return {
    branch: branchIdentity(branch),
    doc: {
      docType: DOC_TYPES[source.docTypeId] || 'quotation',
      status: resolveDocumentStatus({
        documentId: text(form._id) || text(source.documentId),
        docType: DOC_TYPES[source.docTypeId],
        status: form.status,
        payment: form.payment,
        netPrice: form.net_price
      }),
      plate: {
        characters: text(form.car_plate_character),
        number: text(form.car_plate_number),
        province: text(form.car_plate_province)
      },
      phone: text(form.customer_phone_number),
      carType: carTypeLabel(form.car_type, source.carTypeList),
      // The page has one customer name field; the customer is the car owner.
      ownerName: text(form.customer_name) === WALK_IN_CUSTOMER ? '' : text(form.customer_name),
      items,
      totals: {
        subtotal: Number(totals.total_price) || 0,
        discount: Number(totals.total_discount) || 0,
        vat: Number(totals.vat_price) || 0,
        net: Number(totals.net_price) || 0
      }
    },
    qrEnabled: isPaymentQrOnCustomerDisplay(source.docTypeId, branch.setting && branch.setting.show_qr_code_payment),
    // Saved payment destination: the loaded document's own snapshot, else the one
    // the backend returned with the print data right after a save. Absent for
    // legacy documents, which fall back to the Branch's uploaded image.
    snapshot: form.qr_payment_snapshot || (printBill && printBill.qr_payment_snapshot) || null,
    legacyImageUrl: text(branch.qr_code_payment)
  };
}
